import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useRef, useState, type FC } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import { SupabaseTagsService } from "./server/supabase-tags-service";
import type { Tag } from "./types";

export const TagEditDialog: FC<{
  open: boolean;
  handleClose: () => void;
  tag: Tag;
  reload: () => void;
}> = ({ open, handleClose, tag, reload }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const [tagName, setTagName] = useState<string>(tag.name);

  const handleSave = useSupabaseCallback(
    async ({ supabase }) => {
      if (saving) return;
      setSaving(true);

      const { error } = await new SupabaseTagsService(supabase).update(tag.id, {
        name: tagName.trim(),
      });

      if (error) {
        if (error?.message.toLowerCase().includes("duplicate key")) {
          toast.error(t("Tag name must be unique"));
        } else {
          toast.error(t("Saving tag name failed, please try again"));
        }
        setSaving(false);
        return;
      }

      toast.success(t("Tag name updated"));
      reload();
      setSaving(false);
      handleClose();
    },
    [handleClose, reload, saving, t, tag, tagName],
  );

  const onSaveClick = (): void => {
    void handleSave();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        <Box>{t("Edit Tag")}</Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          inputRef={ref}
          id="tag-name-edit"
          name="tag-name"
          label={t("Tag name")}
          title={t("Tag name")}
          fullWidth
          onChange={(e) => setTagName(e.target.value)}
          value={tagName}
          margin="dense"
        ></TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} data-analytics-id="edit-tag-cancel" variant="text">
          {t("Cancel")}
        </Button>
        <Button
          onClick={onSaveClick}
          data-analytics-id="edit-tag-update"
          variant="text"
          loading={saving}
          disabled={saving || tagName === tag.name || tagName.trim().length === 0}
        >
          {t("Update Tag Name")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
